import moment from "moment";
import { trainingUserEmails } from "./constants";

const SAFE_IMAGE_DATA_URI =
  /^data:image\/(png|jpe?g|gif|webp);base64,[A-Za-z0-9+/]+=*$/i;

export const formatReportedDate = (dateString) => {
  if (dateString !== " ") {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } else {
    return " ";
  }
};

export const formatDate = (ts) => {
  if (!ts) return null;

  const m = moment(ts);
  return m.isValid() ? m.format("DD/MM/YYYY") : null;
};

const DTF = "Do /MMM /YYYY";

export const formatDtAgo = (ts) => {
  const m = moment(ts);
  return `${m.format(DTF)} (${m.fromNow()})`;
};

export const formatClinicalDtAge = (dob) => {
  if (!dob) return "";

  const dobMoment = moment(dob, "DD/MM/YYYY", true);
  if (!dobMoment.isValid()) return "";

  const currentMoment = moment();
  const age = currentMoment.diff(dobMoment, "years");

  return `${dobMoment.format("DD/MM/YYYY")} (${age} years old)`;
};

export const formatDtAge = (dob, isVhLogin = false) => {
  const dobMoment = moment(dob);
  const currentMoment = moment();
  const age = currentMoment.diff(dobMoment, "years");
  return isVhLogin
    ? `${dobMoment.format("DD/MM/YYYY")} (${age} years)`
    : `${dobMoment.format("DD/MM/YYYY")} (${age} years old)`;
};

export const formatDob = (dateString) => {
  const [month, day, year] = dateString.split("/");
  if (!month || !day || !year) {
    throw new Error("Invalid date format. Must be in MM/DD/YYYY format.");
  }
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
};

export const trimSpaces = (inputString) => inputString.trim();

/**
 * Format data by trimming strings.
 * @param {Object} data - The data object to format.
 * @returns {Object} - The formatted data object.
 */
const formatData = (data) => {
  return Object.keys(data).reduce((acc, key) => {
    acc[key] = typeof data[key] === "string" ? data[key].trim() : data[key];
    return acc;
  }, {});
};

export default formatData;

export const flattenObject = (obj, parentKey = "", res = {}) => {
  for (let key in obj) {
    if (
      typeof obj[key] === "object" &&
      obj[key] !== null &&
      !Array.isArray(obj[key])
    ) {
      flattenObject(obj[key], parentKey + key + ".", res);
    } else {
      res[parentKey + key] = obj[key];
    }
  }
  return res;
};

export const formatBarcode = (barcode) => {
  if (!barcode) return "";

  let cleanedBarcode = barcode.replace(/[^a-zA-Z0-9]/g, "");
  let firstSegment = cleanedBarcode
    .slice(0, 3)
    .toUpperCase()
    .replace(/[^A-Z]/g, "");
  let secondSegment = cleanedBarcode
    .slice(3, 6)
    .replace(/[^a-zA-Z]/g, "")
    .toLowerCase();
  let remainingSegment = cleanedBarcode.slice(6, 10);

  if (remainingSegment)
    return `${firstSegment}-${secondSegment}-${remainingSegment}`;
  if (secondSegment) return `${firstSegment}-${secondSegment}`;
  return firstSegment;
};

export const STATUS_MAP = {
  registered: {
    className: " btn-registered",
    label: "Registered: Select Service",
  },
  activated: { className: " btn-in-transit", label: "In Transit" },
  activating: { className: " btn-activating", label: "Activating" },
  created: { className: " btn-activating", label: "Activating" },
  "in-transit": { className: " btn-in-transit", label: "In Transit" },
  processing: { className: " btn-processing", label: "Processing" },
  assigned: { className: " btn-processing", label: "Processing" },
  "results-ready": { className: " btn-result-ready", label: "Results Ready" },
  published: { className: " btn-published", label: "Published" },
  rejected: { className: " btn-rejected", label: "Rejected" },
  cancelled: { className: " btn-rejected", label: "Cancelled" },
  "rejected-internal": { className: " btn-rejected", label: "Rejected" },
  error: { className: " btn-error", label: "Error" },
  quarantined: { className: " btn-activating", label: "Quarantined" },
  "quarantine-cleared": {
    className: " btn-in-transit",
    label: "Quarantine Cleared",
  },
};

// ─── Status Mappings ──────────────────────────────────────────────────────────

/**
 * Shared base entries between statusMapping and clinicalstatusMapping.
 * Extracted once to eliminate duplication.
 */
const BASE_STATUS_ENTRIES = {
  created: { variant: "primary", label: "Registered" },
  pending: { variant: "primary", label: "Select test" },
  processing: { variant: "yellow", label: "Processing" },
  in_transit: { variant: "powder-pink", label: "In Transit" },
  activated: { variant: "powder-pink", label: "In Transit" },
  processing_consultation: { variant: "mint", label: "Processing" },
  processing_mint: { variant: "mint", label: "Processing" },
  processing_raspberry: { variant: "raspberry", label: "Processing" },
  results_ready: { variant: "dark-green", label: "Results Ready" },
  published: { variant: "dark-green", label: "Published" },
  completed: { variant: "dark-green", label: "Completed" },
  activating: { variant: "grey-lilac", label: "Activating" },
  rejected: { variant: "raspberry", label: "Rejected" },
  rejected_internal: { variant: "raspberry", label: "Rejected" },
  archived: { variant: "raspberry", label: "Archived" },
};

export const statusMapping = {
  ...BASE_STATUS_ENTRIES,
  registered: { variant: "primary", label: "registered" }, // lowercase label intentional
  error: { variant: "raspberry", label: "Error" },
};

export const clinicalstatusMapping = {
  ...BASE_STATUS_ENTRIES,
  registered: { variant: "primary", label: "Select test" }, // differs from statusMapping
  error: { variant: "raspberry", label: "Rejected" }, // differs from statusMapping
};

// ─── Timezone / DateTime Helpers ──────────────────────────────────────────────

/**
 * Builds a formatted local ISO datetime string with offset.
 * @param {Date} dateObj - A valid Date object
 * @returns {string} - Formatted datetime like '2025-06-19T10:30:00+05:30'
 */
const formatWithTimezone = (dateObj) => {
  if (Number.isNaN(dateObj.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  const year = dateObj.getFullYear();
  const month = pad(dateObj.getMonth() + 1);
  const day = pad(dateObj.getDate());
  const hours = pad(dateObj.getHours());
  const minutes = pad(dateObj.getMinutes());
  const seconds = pad(dateObj.getSeconds());

  const offset = -dateObj.getTimezoneOffset();
  const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
  const offsetMinutes = pad(Math.abs(offset) % 60);
  const sign = offset >= 0 ? "+" : "-";

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${sign}${offsetHours}:${offsetMinutes}`;
};

/**
 * Shared core for TimeFormatter and localTimeFormatter.
 * @param {string} date
 * @param {string} time
 * @param {boolean} treatAsUTC - if true, appends 'Z' to treat input as UTC
 * @returns {Promise<string>}
 */
const buildFormattedDateTime = (date, time, treatAsUTC = false) => {
  return new Promise((resolve) => {
    if (!date || !time) return resolve("");

    const timeOnly = time.includes("T")
      ? time.split("T")[1].split("Z")[0]
      : time;
    const combined = treatAsUTC
      ? `${date}T${timeOnly}Z`
      : `${date}T${timeOnly}`;

    resolve(formatWithTimezone(new Date(combined)));
  });
};

/**
 * Returns date and time in a formatted manner (local input)
 */
export const TimeFormatter = (date, time) =>
  buildFormattedDateTime(date, time, false);

/**
 * Returns date and time in a formatted manner for local time (UTC input)
 */
export const localTimeFormatter = (date, time) =>
  buildFormattedDateTime(date, time, true);

// ─── Misc Formatters ──────────────────────────────────────────────────────────

/**
 * Returns sample_collection_time in a formatted manner
 */
export const extractTime = (formattedDateTime) => {
  if (!formattedDateTime) return "";
  const timePart = formattedDateTime.split("T")[1];
  return timePart ? timePart.slice(0, 5) : "";
};

export const formatFullName = (name) => {
  if (!name) return "";

  return name
    .trim()
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
/**
 * Returns testtype in a formatted manner
 */
export const formatTestType = (testType) => {
  const testTypeMapping = {
    "Cholesterol Profile": "blood-cholesterol",
    "Energy Profile": "blood-energy",
    "Female Sexual Health": "blood-female-sex-health",
    "General Health": "blood-general",
    "Heart Profile": "blood-heart",
    "Male Sexual Health": "blood-male-sex-health",
    "Menopause Profile": "blood-menopause",
    "Polycystic Ovary Syndrome (PCOS)": "blood-polycystic",
    "Vitamins & Minerals Profile": "blood-vitamins",
    "Weight Management": "blood-weight",
    "Thyroid Profile": "blood-thyroid",
    "Erectile Dysfunction": "blood-erectile",
    "Testosterone Test": "blood-testosterone",
    "Vitamin B12": "blood-vitamin-b12",
    "Vitamin B12 Test": "blood-vitamin-b12",
    "Thrive Panel": "thrive-panel",
  };
  return testTypeMapping[testType] || "Unknown test type";
};

export const formatStudyName = (studyName) => {
  const studyNameMapping = {
    "Thrive Registry": "thrive-registry",
  };
  return studyNameMapping[studyName] || "Unknown study name";
};

/**
 * Formats the rejection date from YYYY-MM-DD to DD-MM-YYYY
 */
export const formatRejectionDate = (dateString) => {
  if (!dateString) return "";
  const [year, month, day] = dateString.split("-");
  return `${day}-${month}-${year}`;
};

export const formatEditDate = (dateString) => {
  if (!dateString) return "";

  const parts = dateString.split("/");
  if (parts.length !== 3) return "";

  let [day, month, year] = parts;

  // pad single digits
  day = day.padStart(2, "0");
  month = month.padStart(2, "0");

  // basic validation
  if (!day || !month || !year) return "";

  return `${year}-${month}-${day}`;
};

/**
 * Generates a time window for the last N and next N minutes
 */
export const generateTimeWindow = (
  intervalInMinutes = 1,
  windowMinutes = 30,
) => {
  const now = new Date();
  now.setSeconds(0, 0);
  const start = new Date(now.getTime() - windowMinutes * 60 * 1000);
  const end = new Date(now.getTime() + windowMinutes * 60 * 1000);
  const times = [];

  for (
    let time = new Date(start);
    time.getTime() <= end.getTime();
    time.setMinutes(time.getMinutes() + intervalInMinutes)
  ) {
    times.push(new Date(time));
  }

  return times;
};

/**
 * Validates if the time is within 30 minutes of the current time.
 */
export const timeWithin30Min = (value) => {
  if (!value) return true;

  const [hh, mm] = value.split(":");
  const entered = new Date();
  entered.setHours(+hh, +mm, 0, 0);

  const now = new Date();
  now.setSeconds(0, 0);

  const lower = new Date(now.getTime() - 30 * 60 * 1000);
  const upper = new Date(now.getTime() + 30 * 60 * 1000);

  return entered >= lower && entered <= upper;
};

export const generate30MinTimeSlots = () => {
  const now = new Date();
  const slots = [];

  for (let i = -30; i <= 30; i += 1) {
    const date = new Date(now.getTime() + i * 60 * 1000);
    const hh = date.getHours().toString().padStart(2, "0");
    const mm = date.getMinutes().toString().padStart(2, "0");
    slots.push(`${hh}:${mm}`);
  }

  return slots;
};

export const getSafeSvg = (src) => {
  if (!src) return null;

  if (src.startsWith("/") || src.startsWith("./")) {
    return src;
  }

  return null;
};

export const getSafeImageSrc = (src) => {
  if (!src || typeof src !== "string") return null;
  const trimmed = src.trim();
  if (
    (trimmed.startsWith("/") && !trimmed.startsWith("//")) ||
    trimmed.startsWith("./")
  ) {
    return trimmed;
  }
  if (SAFE_IMAGE_DATA_URI.test(trimmed)) {
    return trimmed;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol === "https:") return trimmed;
  } catch {
    // Invalid URL, return null
  }
  return null;
};

export const getSafeHref = (href) => {
  if (typeof href !== "string") return null;

  const trimmed = href.trim();

  if (
    (trimmed.startsWith("/") && !trimmed.startsWith("//")) ||
    trimmed.startsWith("./")
  ) {
    return trimmed;
  }

  try {
    const url = new URL(trimmed);

    if (url.protocol === "http:" || url.protocol === "https:") {
      return url.href;
    }
  } catch {
      // catched error. User remains on the current page.
  }

  return null;
};

export const removeLeadingZeros = (value) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  return String(value).replace(/^0+(?=.)/, "");
};

export const formatDateTime = (dateString) => {
  if (!dateString) return "";

  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export const removeCountryCode = (phone) => {
  if (!phone) return "";
  phone = phone.replace(/\s+/g, "");
  if (phone.startsWith("+44")) {
    return phone.slice(3);
  }
  return phone;
};

export const formatCost = (amount) => {
  const num = Number(amount);
  if (Number.isNaN(num)) return "FOC";
  if (num === 0) return "FOC";
  const formatted = num % 1 === 0 ? num.toString() : num.toFixed(2);

  return `£${formatted}`;
};
export const getRangeError = (value, min, max) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  if (min === undefined && max === undefined) {
    return null;
  }

  const num = parseFloat(value);

  if (isNaN(num)) return "Enter a valid number";
  if (min !== undefined && num < min) return `Must be ≥ ${min}`;
  if (max !== undefined && num > max) return `Must be ≤ ${max}`;

  return null;
};

export const validateTableData = (data, columns) => {
  const errors = {};

  data.forEach((row) => {
    columns.forEach((column) => {
      const value = row[column.dataField];

      const error = getRangeError(value, column.min, column.max);

      if (error) {
        errors[`${row.id}_${column.dataField}`] = error;
      }
    });
  });

  return errors;
};

export const stripId = (arr = []) => arr.map(({ id, ...rest }) => rest);

export const addIds = (arr = [], startId = 1) =>
  arr.map((item, idx) => ({ id: startId + idx, ...item }));

export const toSingleObject = (arr = []) => stripId(arr)[0] || {};

export const toSingleRowArray = (obj, id = 1) =>
  obj && Object.keys(obj).length ? [{ id, ...obj }] : null;

// Training mode is only available on local and staging instances
const isTrainingEnvironment = () =>
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_ENVIRONMENT === "staging";

// Checks whether the logged-in user is allowed to use training mode
export const isTrainingUser = () => {
  if (!isTrainingEnvironment()) return false;
  const email = (sessionStorage.getItem("email") || "").trim().toLowerCase();
  return trainingUserEmails.includes(email);
};
